import { useEffect, useMemo, useState } from "react"
import { Localized } from "@fluent/react"
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
} from "@mui/lab"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"

import { AppId } from "../../../../app/appTypes"
import { CloseButton } from "../../../../app/components/CloseButton"
import { useAppDispatch } from "../../../../app/hooks"
import {
    DocumentWorkflowTimelineItemDto,
    loadDocumentWorkflowTimeline,
} from "../documentWorkflowApi"
import {
    formatDateTime,
    getStageUiType,
    mapWorkflowActionTypeLabel,
} from "../documentWorkflowHelpers"
import { alpha } from "@mui/material/styles"

type DocumentWorkflowTimelineDialogProps = {
    open: boolean
    documentId?: AppId | null
    onClose: () => void
}

type TimelineViewMode = "table" | "timeline"

const normalizeEventType = (value?: string | null) => {
    return String(value ?? "").trim().toUpperCase()
}

const normalizeActionResult = (value?: string | null) => {
    return String(value ?? "").trim().toUpperCase()
}

const getTimelineEventColor = (eventType: string): "primary" | "success" | "warning" | "error" | "info" | "grey" => {
    if (eventType === "ACTION_COMPLETED" || eventType === "STAGE_COMPLETED") {
        return "success"
    }

    if (eventType === "ACTION_CANCELLED" || eventType === "STAGE_CANCELLED") {
        return "error"
    }

    if (eventType === "ACTION_PENDING") {
        return "warning"
    }

    if (eventType === "STAGE_STARTED") {
        return "info"
    }

    return "grey"
}

const getTimelineEventLocalizationId = (eventType: string) => {
    switch (eventType) {
        case "ACTION_COMPLETED":
            return "document-workflow-timeline-event-action-completed"
        case "ACTION_CANCELLED":
            return "document-workflow-timeline-event-action-cancelled"
        case "ACTION_PENDING":
            return "document-workflow-timeline-event-action-pending"
        case "STAGE_STARTED":
            return "document-workflow-timeline-event-stage-started"
        case "STAGE_COMPLETED":
            return "document-workflow-timeline-event-stage-completed"
        case "STAGE_CANCELLED":
            return "document-workflow-timeline-event-stage-cancelled"
        default:
            return null
    }
}

const getActionResultLabelPl = (actionResult?: string | null) => {
    const normalizedActionResult = normalizeActionResult(actionResult)

    if (normalizedActionResult === "A") {
        return "Zatwierdzono"
    }

    if (normalizedActionResult === "R") {
        return "Odrzucono"
    }

    if (!normalizedActionResult) {
        return "-"
    }

    return actionResult ?? "-"
}

const getTimelineEventLabelPl = (eventType: string, actionResult?: string | null) => {
    const resultLabel = getActionResultLabelPl(actionResult)

    if (resultLabel === "Zatwierdzono" || resultLabel === "Odrzucono") {
        return resultLabel
    }

    switch (eventType) {
        case "ACTION_COMPLETED":
            return "Akcja wykonana"
        case "ACTION_CANCELLED":
            return "Akcja anulowana"
        case "ACTION_PENDING":
            return "Akcja oczekująca"
        case "STAGE_STARTED":
            return "Etap rozpoczęty"
        case "STAGE_COMPLETED":
            return "Etap zakończony"
        case "STAGE_CANCELLED":
            return "Etap anulowany"
        default:
            return eventType
    }
}

const getTimelineEventTableAccent = (eventColor: "primary" | "success" | "warning" | "error" | "info" | "grey") => {
    if (eventColor === "success") {
        return "#2e7d32"
    }

    if (eventColor === "error") {
        return "#d32f2f"
    }

    if (eventColor === "warning") {
        return "#ed6c02"
    }

    if (eventColor === "info") {
        return "#0288d1"
    }

    if (eventColor === "primary") {
        return "#1976d2"
    }

    return "#6b7280"
}

const getTimelineEventChipColor = (eventColor: "primary" | "success" | "warning" | "error" | "info" | "grey") => {
    if (eventColor === "grey") {
        return "default" as const
    }

    return eventColor
}

const getStageLabel = (item: DocumentWorkflowTimelineItemDto) => {
    if (!item.stageName) {
        return "-"
    }

    const stageType = getStageUiType({
        id: item.sourceWorkflowStageId,
        name: item.stageName,
        description: "",
        type: item.stageType,
        status: "NOT_STARTED",
        order: 0,
        isCurrent: false,
        documentTeamMemberIds: [],
        members: [],
        actions: [],
    })

    if (stageType === "APPROVAL") {
        return `${item.stageName} (zatwierdzanie)`
    }

    if (stageType === "REVIEW") {
        return `${item.stageName} (opiniowanie)`
    }

    return `${item.stageName} (edycja)`
}

export const DocumentWorkflowTimelineDialog = ({
    open,
    documentId,
    onClose,
}: DocumentWorkflowTimelineDialogProps) => {
    const dispatch = useAppDispatch()
    const [items, setItems] = useState<DocumentWorkflowTimelineItemDto[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [serverError, setServerError] = useState("")
    const [viewMode, setViewMode] = useState<TimelineViewMode>("table")

    useEffect(() => {
        if (open) {
            setViewMode("table")
        }
    }, [open])

    useEffect(() => {
        if (!open) {
            return
        }

        if (!documentId) {
            setItems([])
            setServerError("error")
            return
        }

        let active = true

        const loadTimeline = async () => {
            setIsLoading(true)
            setServerError("")

            const result = await dispatch(loadDocumentWorkflowTimeline(String(documentId)))

            if (!active) {
                return
            }

            if (loadDocumentWorkflowTimeline.fulfilled.match(result)) {
                setItems(result.payload)
            } else {
                let errorMsg = "error"

                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem

                    if (problem?.title) {
                        errorMsg = problem.title
                    }
                }

                setServerError(errorMsg)
            }

            setIsLoading(false)
        }

        void loadTimeline()

        return () => {
            active = false
        }
    }, [dispatch, documentId, open])

    const sortedItems = useMemo(() => {
        return [...items].sort((left, right) => {
            const leftTime = new Date(left.date ?? 0).getTime()
            const rightTime = new Date(right.date ?? 0).getTime()

            if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
                return 0
            }

            return rightTime - leftTime
        })
    }, [items])

    return <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle sx={{ bgcolor: "action.hover" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryOutlinedIcon fontSize="small" />
                <Localized id="document-workflow-timeline-title">
                    <span>_Pelna historia workflow</span>
                </Localized>
            </Box>
        </DialogTitle>

        <DialogContent dividers>
            {serverError && <Alert sx={{ mt: 1 }} onClose={() => setServerError("")} severity="error">
                <Localized id={serverError}>
                    <span>_Server error</span>
                </Localized>
            </Alert>}

            {!isLoading && sortedItems.length > 0 && <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    sx={{
                        "& .MuiToggleButton-root": {
                            minHeight: 28,
                            px: 1.2,
                            fontSize: 12,
                            textTransform: "none",
                        },
                    }}
                    onChange={(_, nextMode: TimelineViewMode | null) => {
                        if (nextMode) {
                            setViewMode(nextMode)
                        }
                    }}
                >
                    <ToggleButton value="table">
                        Tabela
                    </ToggleButton>
                    <ToggleButton value="timeline">
                        Timeline
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>}

            {isLoading
                ? <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={28} />
                </Box>
                : sortedItems.length === 0
                    ? <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                        <Localized id="document-workflow-timeline-empty">
                            <span>_Brak zdarzen workflow do pokazania.</span>
                        </Localized>
                    </Alert>
                    : viewMode === "table"
                        ? <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "grey.100" }}>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>Data</TableCell>
                                        <TableCell>Zdarzenie</TableCell>
                                        <TableCell>Etap</TableCell>
                                        <TableCell>Uzytkownik</TableCell>
                                        <TableCell>Akcja</TableCell>
                                        <TableCell>Wynik</TableCell>
                                        <TableCell>Komentarz</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedItems.map((item, index) => {
                                        const eventType = normalizeEventType(item.eventType)
                                        const eventColor = getTimelineEventColor(eventType)
                                        const chipColor = getTimelineEventChipColor(eventColor)
                                        const eventLabel = getTimelineEventLabelPl(eventType, item.actionResult)
                                        const accentColor = getTimelineEventTableAccent(eventColor)
                                        const userDisplay = item.fullName || item.userName || "-"

                                        return <TableRow
                                            key={item.id || `${eventType}-${item.sourceWorkflowStageId}-${index}`}
                                            hover
                                            sx={{
                                                borderLeft: "4px solid",
                                                borderLeftColor: accentColor,
                                                bgcolor: index % 2 === 0
                                                    ? alpha(accentColor, 0.04)
                                                    : "transparent",
                                                "& td": {
                                                    borderColor: alpha(accentColor, 0.18),
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {formatDateTime(item.date)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={chipColor}
                                                    variant={eventColor === "grey" ? "outlined" : "filled"}
                                                    label={eventLabel}
                                                    sx={{
                                                        fontWeight: 600,
                                                        maxWidth: "100%",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{getStageLabel(item)}</TableCell>
                                            <TableCell>{userDisplay}</TableCell>
                                            <TableCell>
                                                {item.actionType !== null && item.actionType !== undefined
                                                    ? mapWorkflowActionTypeLabel(item.actionType)
                                                    : "-"}
                                            </TableCell>
                                            <TableCell>{getActionResultLabelPl(item.actionResult)}</TableCell>
                                            <TableCell>{item.comment || "-"}</TableCell>
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        : <Timeline sx={{ m: 0, p: 0 }}>
                        {sortedItems.map((item, index) => {
                            const eventType = normalizeEventType(item.eventType)
                            const eventColor = getTimelineEventColor(eventType)
                            const eventLabel = getTimelineEventLabelPl(eventType, item.actionResult)
                            const userDisplay = item.fullName || item.userName || "-"

                            return <TimelineItem key={item.id || `${eventType}-${item.sourceWorkflowStageId}-${index}`}>
                                <TimelineOppositeContent
                                    sx={{ flex: 0.25, px: 0, color: "text.secondary", fontSize: 12, whiteSpace: "nowrap" }}
                                >
                                    {formatDateTime(item.date)}
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    <TimelineDot color={eventColor} variant={eventColor === "grey" ? "outlined" : "filled"} />
                                    {index < sortedItems.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ pb: 2.75, pr: 0 }}>
                                    <Stack spacing={0.65}>
                                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {eventLabel}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`Iteracja ${item.iterationNo}`}
                                                variant="outlined"
                                            />
                                        </Box>

                                        <Typography variant="caption" color="text.secondary">
                                            {getStageLabel(item)}
                                        </Typography>

                                        <Typography variant="body2">
                                            {userDisplay}
                                        </Typography>

                                        {item.actionType !== null && item.actionType !== undefined && <Typography variant="caption" color="text.secondary">
                                            {mapWorkflowActionTypeLabel(item.actionType)}
                                        </Typography>}

                                        {item.actionResult && <Typography variant="caption" color="text.secondary">
                                            {`Wynik: ${getActionResultLabelPl(item.actionResult)}`}
                                        </Typography>}

                                        {item.comment && <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                                            {`\"${item.comment}\"`}
                                        </Typography>}
                                    </Stack>
                                </TimelineContent>
                            </TimelineItem>
                        })}
                    </Timeline>}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
            <CloseButton loading={false} onClick={onClose} />
        </DialogActions>
    </Dialog>
}
